import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Filter, Search, Plus, Download, Upload, MoreHorizontal,
  SlidersHorizontal, Grid3X3, List, RefreshCw
} from 'lucide-react';
import { MOCK_ASSETS, ASSET_CATEGORIES, ASSET_STATUSES, LOCATIONS, DEPARTMENTS } from '../../data/mockData';
import AssetFilters from './AssetFilters';
import AssetTable from './AssetTable';
import AssetGrid from './AssetGrid';
import AssetDetails from './AssetDetails';

const AssetInventory = () => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    categories: [],
    statuses: [],
    locations: [],
    departments: [],
    dateRange: null,
    valueRange: { min: null, max: null }
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  const [selectedAssets, setSelectedAssets] = useState([]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAssets = MOCK_ASSETS.filter(asset => {
    if (filters.search && !asset.name.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.categories.length && !filters.categories.includes(asset.category)) {
      return false;
    }
    if (filters.statuses.length && !filters.statuses.includes(asset.status)) {
      return false;
    }
    if (filters.locations.length && !filters.locations.includes(asset.location)) {
      return false;
    }
    if (filters.departments.length && !filters.departments.includes(asset.department)) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return a[sortConfig.key] > b[sortConfig.key] ? direction : -direction;
  });

  const bulkActions = [
    { label: 'Update Status', action: () => {} },
    { label: 'Schedule Maintenance', action: () => {} },
    { label: 'Generate Report', action: () => {} },
    { label: 'Delete Selected', action: () => {}, danger: true }
  ];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex-1 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search assets..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43302b]/20"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm">Filters</span>
          </button>

          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 ${viewMode === 'table' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-[#43302b] text-white rounded-lg hover:bg-[#43302b]/90">
            <Plus className="w-4 h-4" />
            <span className="text-sm">Add Asset</span>
          </button>

          <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <AssetFilters
            filters={filters}
            setFilters={setFilters}
            categories={ASSET_CATEGORIES}
            statuses={ASSET_STATUSES}
            locations={LOCATIONS}
            departments={DEPARTMENTS}
          />
        </motion.div>
      )}

      {/* Bulk Actions */}
      {selectedAssets.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between bg-[#43302b] text-white px-4 py-3 rounded-lg"
        >
          <div className="flex items-center gap-3">
            <span className="text-sm">{selectedAssets.length} items selected</span>
            <button className="text-sm underline" onClick={() => setSelectedAssets([])}>
              Clear selection
            </button>
          </div>
          <div className="flex gap-2">
            {bulkActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className={`px-3 py-1 rounded-lg text-sm ${
                  action.danger
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {viewMode === 'table' ? (
          <AssetTable
            assets={filteredAssets}
            sortConfig={sortConfig}
            onSort={handleSort}
            selectedAssets={selectedAssets}
            onSelectAsset={setSelectedAsset}
            onSelectAssets={setSelectedAssets}
          />
        ) : (
          <AssetGrid
            assets={filteredAssets}
            selectedAssets={selectedAssets}
            onSelectAsset={setSelectedAsset}
            onSelectAssets={setSelectedAssets}
          />
        )}
      </div>

      {/* Asset Details Modal */}
      {selectedAsset && (
        <AssetDetails
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
        />
      )}
    </div>
  );
};

export default AssetInventory; 